/**
 * Helpers for the supplied_value ad template var.
 *
 * A supplied_value var renders a value (typically a promo code) drawn from the
 * company's pool of supplied values at the time the ad is produced. Its config
 * lives entirely on the var's metadata:
 *
 *   company_group_id - required - which group of values to draw from
 *   start / end      - optional - ISO8601, the window in which values may be
 *                                 allocated
 *   ttl_days         - optional - positive integer, days from allocation until
 *                                 the value expires
 *   display_value    - optional - a representative value shown on the canvas
 *                                 and in the preview in place of a real
 *                                 allocation, so the designer can check sizing
 *   export_key       - optional - names a template param under which this var
 *                                 publishes its computed expiry date, for a
 *                                 separate var to render. Requires ttl_days
 *
 * The var renders the code alone. The expiry date is published under export_key
 * rather than appended, so the code and the date are independent objects with
 * their own position and wording.
 *
 * The validation here mirrors the server's rules so problems surface in the
 * editor rather than on save.
 */

/**
 * Shown on the canvas when the designer has not supplied a representative
 * value of their own.
 */
export const SUPPLIED_VALUE_PLACEHOLDER = '[promo code]';

/**
 * The text a supplied value var should render on the canvas.
 *
 * Mirrors the server's preview rule: a blank or absent display value falls back
 * to the generic placeholder rather than rendering an empty string. The server
 * trims before rendering, so trim here too and the canvas matches the preview.
 *
 * The expiry date is deliberately absent. The server publishes it under the
 * var's export_key for a separate var to render, so this object shows the code
 * alone.
 */
export const suppliedValueDisplayText = metadata => {
  const displayValue = metadata?.display_value;

  if (typeof displayValue !== 'string') return SUPPLIED_VALUE_PLACEHOLDER;

  const trimmed = displayValue.trim();

  return trimmed === '' ? SUPPLIED_VALUE_PLACEHOLDER : trimmed;
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const padDatePart = part => String(part).padStart(2, '0');

/**
 * Formats an expiry date as the server does, MM/DD/YYYY.
 *
 * The server adds ttl_days to a UTC instant and formats that instant's UTC
 * parts, so read UTC parts back here too. Using local parts would shift the
 * date by a day for anyone behind UTC and the editor would disagree with the
 * printed ad.
 */
const formatExpiryDate = date =>
  `${padDatePart(date.getUTCMonth() + 1)}/${padDatePart(
    date.getUTCDate()
  )}/${date.getUTCFullYear()}`;

/**
 * Parses ttl_days, accepting the string a form yields as well as a number.
 * Returns null for anything absent or not a positive integer, matching the
 * server's rule that only a positive integer counts.
 */
const parseTtlDays = value => {
  if (value === undefined || value === null || value === '') return null;

  const trimmed = String(value).trim();
  if (!/^\d+$/.test(trimmed)) return null;

  const parsed = parseInt(trimmed, 10);

  return parsed >= 1 ? parsed : null;
};

/**
 * A representative expiry date for the given ttl, as the consuming var would
 * render it, or null when the ttl is absent or invalid.
 *
 * The real date is counted from the day the code is issued, which is unknown in
 * the editor. The server's preview dates it from now for the same reason, so
 * this is illustrative rather than a prediction. `now` is injectable for tests.
 */
export const sampleExpiryDate = (ttlDays, now = new Date()) => {
  const parsed = parseTtlDays(ttlDays);

  if (parsed === null) return null;

  return formatExpiryDate(new Date(now.getTime() + parsed * MS_PER_DAY));
};

/**
 * The export keys published by a template's supplied_value vars, in the order
 * the vars appear, deduplicated.
 *
 * Used to offer a designer the keys they can consume from a text var, since the
 * two ends are free text that have to match exactly.
 */
export const collectExportKeys = (objects = []) => {
  const keys = objects
    .filter(object => object?.type === 'SuppliedValue')
    .map(object => object?.metadata?.export_key)
    .filter(key => typeof key === 'string' && key.trim() !== '')
    .map(key => key.trim());

  return [...new Set(keys)];
};

/**
 * Converts a stored ISO8601 datetime to the yyyy-mm-dd a date input expects.
 * Returns '' for anything absent or unparseable so the input stays blank
 * rather than rendering "Invalid Date".
 */
export const toDateInputValue = value => {
  if (!value) return '';

  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) return '';

  // the stored value is UTC, so read back the UTC parts to avoid the date
  // shifting a day for users behind or ahead of UTC.
  const year = parsed.getUTCFullYear();
  const month = String(parsed.getUTCMonth() + 1).padStart(2, '0');
  const day = String(parsed.getUTCDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

/**
 * Converts a yyyy-mm-dd date input value to the ISO8601 datetime the server
 * stores. `bound` decides which end of the day the date maps to, so that a
 * date range entered in the editor is inclusive of both days.
 *
 * Returns undefined for a blank date, which clears the key from metadata.
 */
export const toIso8601 = (value, bound = 'start') => {
  if (!value) return undefined;

  const time = bound === 'end' ? '23:59:59' : '00:00:00';
  const parsed = new Date(`${value}T${time}Z`);
  if (isNaN(parsed.getTime())) return undefined;

  return parsed.toISOString().replace('.000Z', 'Z');
};

/**
 * Validates the supplied value config as entered in the editor.
 *
 * Takes the raw form values (dates as yyyy-mm-dd, ttl as a string) and returns
 * an object keyed by field name containing any messages. An empty object means
 * the config is valid.
 */
export const validateSuppliedValueConfig = ({
  company_group_id,
  start,
  end,
  ttl_days,
  display_value,
  export_key,
}) => {
  const errors = {};

  // blank is allowed and means "publishes nothing", so as with display_value
  // the only thing to reject outright is a non-string.
  if (
    export_key !== undefined &&
    export_key !== null &&
    typeof export_key !== 'string'
  ) {
    errors.export_key = 'Enter a text value.';
  } else if (
    typeof export_key === 'string' &&
    export_key.trim() !== '' &&
    parseTtlDays(ttl_days) === null
  ) {
    // the server rejects this: with no ttl there is no date to publish, so the
    // var reading the name would never resolve. Surfaced on the export key,
    // since that is the field the designer just filled in.
    errors.export_key =
      'Set "Expires after" as well — there is no expiry date to show without it.';
  }

  // blank is allowed and means "use the generic placeholder", so the only
  // thing to reject is a non-string, which the server refuses.
  if (
    display_value !== undefined &&
    display_value !== null &&
    typeof display_value !== 'string'
  ) {
    errors.display_value = 'Enter a text value.';
  }

  if (!company_group_id || !String(company_group_id).trim()) {
    errors.company_group_id = 'A code group is required.';
  }

  if (start && end) {
    const startDt = new Date(`${start}T00:00:00Z`);
    const endDt = new Date(`${end}T23:59:59Z`);

    if (
      !isNaN(startDt.getTime()) &&
      !isNaN(endDt.getTime()) &&
      endDt <= startDt
    ) {
      errors.end = 'The end date must be after the start date.';
    }
  }

  if (ttl_days !== undefined && ttl_days !== null && ttl_days !== '') {
    const trimmed = String(ttl_days).trim();
    // the server accepts a positive integer only, so reject decimals, zero,
    // negatives and anything non-numeric before it gets that far.
    if (!/^\d+$/.test(trimmed) || parseInt(trimmed, 10) < 1) {
      errors.ttl_days = 'Enter a whole number of days greater than zero.';
    }
  }

  return errors;
};

/**
 * True when the var has enough config to produce a value at fulfillment time.
 * Used to flag vars that would silently render nothing.
 */
export const isSuppliedValueConfigured = metadata =>
  Object.keys(
    validateSuppliedValueConfig({
      company_group_id: metadata?.company_group_id,
      start: toDateInputValue(metadata?.start),
      end: toDateInputValue(metadata?.end),
      ttl_days:
        metadata?.ttl_days === undefined || metadata?.ttl_days === null
          ? ''
          : String(metadata.ttl_days),
    })
  ).length === 0;
